import { Checkbox, FormControlLabel, Grid, MenuItem, TextField, Typography } from '@freighthub/silhouette-core'
import { IQuoteRequest } from 'features/BookingFlow'
import { ChangeEvent, FC, useMemo } from 'react'
import { useController, useFormState } from 'react-hook-form'
import { ISO } from 'service/ISO'
import { useI18n } from 'utils/localisation'
import { z } from 'zod'

import {
  DefaultValuesBehaviour,
  FormFieldsBehaviour,
  FormSchemaBehaviour,
  MQFields,
  ServiceBehaviour,
  SummaryBehaviour,
} from './MQFormContentProcessor'
import { MQService } from './MQServices'

const MQBuyersConsolidationSummaryContent: FC = () => {
  const { translate } = useI18n()

  return <>{translate('manual_quote-buyers_consolidation-section-description')}</>
}
export class MQBuyersConsolidationSummaryBehaviour implements SummaryBehaviour {
  type = 'buyers-consolidation-summary'
  content = (<MQBuyersConsolidationSummaryContent />)
}

const BuyersConsolidationFormFields: FC = () => {
  const {
    field: { value, onChange },
  } = useController<MQFields, 'additionalInfo'>({
    name: 'additionalInfo',
  })
  const { translate } = useI18n()

  return (
    <Grid xs={12}>
      <TextField
        value={value}
        placeholder={translate('manual_quote-additional_details-buyers_consolidation')}
        onChange={onChange}
        fullWidth
        multiline
        inputProps={{
          'aria-label': translate('manual_quote-additional_details-buyers_consolidation'),
        }}
      />
    </Grid>
  )
}

export class MQBuyersConsolidationFormFieldsBehaviour implements FormFieldsBehaviour {
  type = 'buyers-consolidation-form-fields'
  content = (<BuyersConsolidationFormFields />)
}

const VARIOUS = 'VARIOUS'
const availableIncoterms: ISO.IncoTermsCode[] = ['EXW', 'FCA_T', 'FOB']

export type LeanBuyersConsolidationServicesFieldsProps = {
  incoterms: ISO.IncoTermsCode[]
  onIncotermChange: (incoterms: ISO.IncoTermsCode[]) => void
  numberOfShipmentsError?: boolean
  numberOfShipments: number
  onNumberOfShipmentsChange: (numberOfShipments: number) => void
  numberOfShipmentsUnknown: boolean
  onNumberOfShipmentsUnknownChange: (unknown: boolean) => void
}
export const LeanBuyersConsolidationServicesFields: FC<LeanBuyersConsolidationServicesFieldsProps> = ({
  incoterms,
  onIncotermChange,
  numberOfShipments,
  numberOfShipmentsError,
  onNumberOfShipmentsChange,
  numberOfShipmentsUnknown,
  onNumberOfShipmentsUnknownChange,
}) => {
  const { translate } = useI18n()
  const handleIncotermChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    if (value === VARIOUS) {
      onIncotermChange([])
    } else {
      onIncotermChange([value])
    }
  }

  const incotermValue = useMemo(() => {
    if (incoterms.length === 0) {
      return ''
    }
    const incoterm = incoterms[0]
    return incoterm
  }, [incoterms])

  return (
    <Grid container spacing={2}>
      <Grid xs={12}>
        <FormControlLabel
          label={translate('manual_quote-buyers_consolidation-label-number_of_shipments_not_clear')}
          control={
            <Checkbox
              checked={numberOfShipmentsUnknown}
              onChange={(event) => onNumberOfShipmentsUnknownChange(event.target.checked)}
            />
          }
        />
      </Grid>
      {!numberOfShipmentsUnknown && (
        <Grid xs={12}>
          <TextField
            type="number"
            value={numberOfShipments}
            onChange={(event) => onNumberOfShipmentsChange(Number(event.target.value))}
            inputProps={{
              min: '2',
              max: '20',
              step: '1',
              'aria-label': translate('manual_quote-buyers_consolidation-label-number_of_shipments'),
            }}
            fullWidth
            margin="normal"
            required
            error={numberOfShipmentsError}
            helperText={
              numberOfShipmentsError
                ? translate('manual_quote-buyers_consolidation-number_of_shipments-invalid_number_of_shipments_error')
                : translate('manual_quote-buyers_consolidation-helper_text-number_of_shipments')
            }
            placeholder={translate('manual_quote-buyers_consolidation-placeholder-number_of_shipments')}
            label={translate('manual_quote-buyers_consolidation-label-number_of_shipments')}
          />
        </Grid>
      )}
      <Grid xs={12}>
        <TextField
          select
          required
          value={incotermValue}
          onChange={handleIncotermChange}
          fullWidth
          inputProps={{
            'aria-label': translate('manual_quote-buyers_consolidation-label-incoterms'),
          }}
          margin="normal"
          label={translate('manual_quote-buyers_consolidation-label-incoterms')}
        >
          <MenuItem value={VARIOUS}>
            {translate('manual_quote-buyers_consolidation-default_value-various_incoterms')}
          </MenuItem>
          {availableIncoterms.map((incoterm) => (
            <MenuItem key={incoterm} value={incoterm}>
              {incoterm}
            </MenuItem>
          ))}
        </TextField>
        <Typography variant="caption">
          {translate('manual_quote-buyers_consolidation-helper_text-incoterms')}
        </Typography>
      </Grid>
    </Grid>
  )
}

export const BuyersConsolidationServicesFields: FC = () => {
  const { field: numberOfShipmentsField } = useController<MQFields, 'buyersConsolidation.numberOfShipments'>({
    name: 'buyersConsolidation.numberOfShipments',
  })

  const { field: incotermField } = useController<MQFields, 'buyersConsolidation.incoterms'>({
    name: 'buyersConsolidation.incoterms',
    defaultValue: [],
  })
  const { field: numberOfShipmentsUnknownField } = useController<
    MQFields,
    'buyersConsolidation.numberOfShipmentsUnknown'
  >({
    name: 'buyersConsolidation.numberOfShipmentsUnknown',
  })
  const { errors } = useFormState<MQFields>()

  return (
    <LeanBuyersConsolidationServicesFields
      incoterms={incotermField.value}
      onIncotermChange={incotermField.onChange}
      numberOfShipments={numberOfShipmentsField.value ?? 0}
      numberOfShipmentsError={!!errors.buyersConsolidation?.numberOfShipments}
      onNumberOfShipmentsChange={numberOfShipmentsField.onChange}
      numberOfShipmentsUnknown={numberOfShipmentsUnknownField.value}
      onNumberOfShipmentsUnknownChange={numberOfShipmentsUnknownField.onChange}
    />
  )
}

export class MQBuyersConsolidationFormSchemaBehaviour implements FormSchemaBehaviour {
  private knownNumberOfShipmentsSchema = z.object({
    enabled: z.boolean(),
    numberOfShipmentsUnknown: z.literal(false),
    numberOfShipments: z.number().int().min(2).max(20),
    incoterms: z.string().array().nonempty() as z.ZodType<string[]>,
  })

  private unknownNumberOfShipmentsSchema = z.object({
    enabled: z.boolean(),
    numberOfShipmentsUnknown: z.literal(true),
    incoterms: z.string().array().nonempty() as z.ZodType<string[]>,
  })

  private buyersConsolidationSchema = z.discriminatedUnion('numberOfShipmentsUnknown', [
    this.unknownNumberOfShipmentsSchema,
    this.knownNumberOfShipmentsSchema,
  ])

  schema = z.object({
    buyersConsolidation: this.buyersConsolidationSchema,
    additionalInfo: z.string().optional(),
  })
}

const MQBuyersConsolidationServicesContent: FC = () => {
  const { translate } = useI18n()

  return (
    <MQService
      title={translate('manual_quote-buyers_consolidation-section-heading')}
      value={<BuyersConsolidationServicesFields />}
    />
  )
}
export class MQBuyersConsolidationServicesBehaviour implements ServiceBehaviour {
  type = 'buyers-consolidation-services'
  content = (<MQBuyersConsolidationServicesContent />)
}

export class MQBuyersConsolidationDefaultValuesBehaviour implements DefaultValuesBehaviour {
  constructor(public quoteRequest: IQuoteRequest) {}
  defaultValues: Partial<MQFields> = {
    buyersConsolidation: {
      ...(this.quoteRequest.buyersConsolidation ?? {
        enabled: true,
        incoterms: [],
      }),
      numberOfShipmentsUnknown: false,
    },
  }
}
